/**
 * navigation.js — safe external-URL and "open in maps" helpers.
 *
 * The customer app opens two kinds of links:
 *
 *   1. External web pages (Terms, Privacy, Support, external
 *      marketing). On web these are plain `window.open` calls with the
 *      standard `noopener` protections; on native the Capacitor
 *      Browser plugin opens an in-app Safari View Controller / Custom
 *      Tab so users don't leave the app to read the Terms.
 *
 *   2. Directions to a charger. On web we hand off to Google Maps'
 *      universal URL; on iOS we default to Apple Maps (with a Google
 *      Maps fallback if the user has the app installed); on Android
 *      we use Google's `geo:` intent so the user's default maps app
 *      wins.
 *
 * All URL inputs are validated before opening. Untrusted schemes are
 * refused — the app never opens `javascript:`, `data:`, `file:` or any
 * scheme it doesn't recognise. Deep-link handling is a separate module
 * (see `src/platform/deepLinks.js`).
 */
import { isNative, isIOS, isAndroid } from './platform.js';

const SAFE_URL_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/**
 * Sanity-check a URL before opening. Returns the normalised URL string
 * on success, `null` on rejection. Never throws.
 */
export function normaliseExternalUrl(input) {
  if (typeof input !== 'string' || !input.trim()) return null;
  const trimmed = input.trim();
  try {
    // Base only matters when the caller passes a relative path — in
    // practice callers always pass absolute URLs, but the base keeps
    // us safe from `//example.com` protocol-relative shorthand.
    const parsed = new URL(trimmed, 'https://greencharge.app');
    if (!SAFE_URL_SCHEMES.has(parsed.protocol)) return null;
    return parsed.toString();
  } catch { return null; }
}

let _browser = null;
async function browserPlugin() {
  if (_browser) return _browser;
  const mod = await import('@capacitor/browser');
  _browser = mod.Browser;
  return _browser;
}

/**
 * Open an external URL. On native the Capacitor Browser plugin uses an
 * in-app browser view so authentication returns to the app cleanly on
 * dismiss; `mailto:` and `tel:` are handed off to the OS handler.
 *
 * @returns {Promise<boolean>} true when the OS accepted the request.
 */
export async function openExternal(url) {
  const safe = normaliseExternalUrl(url);
  if (!safe) return false;

  const parsed = new URL(safe);
  const isHttp = parsed.protocol === 'http:' || parsed.protocol === 'https:';

  if (isNative() && isHttp) {
    try {
      const browser = await browserPlugin();
      await browser.open({ url: safe, presentationStyle: 'popover' });
      return true;
    } catch { /* fall through to window.open */ }
  }

  // Web + mailto/tel path
  try {
    if (typeof window === 'undefined') return false;
    const opened = window.open(safe, isHttp ? '_blank' : '_self', 'noopener,noreferrer');
    return !!opened || parsed.protocol !== 'http:';
  } catch { return false; }
}

// ────────────────────────────────────────────────────────────────
// "Open in Maps" for a charger
// ────────────────────────────────────────────────────────────────

function pickMapsUrl({ lat, lng, label }) {
  const encodedLabel = encodeURIComponent(label || 'GreenCharge charger');
  if (isIOS()) {
    // Apple Maps: `?ll=lat,lng&q=Label` — opens in Apple Maps on iOS,
    // falls back to Apple Maps web on macOS Safari.
    return `https://maps.apple.com/?ll=${lat},${lng}&q=${encodedLabel}`;
  }
  if (isAndroid()) {
    // `geo:` intent — Android launches the user's default maps app.
    // Include the `q=` query so Google Maps drops a pin instead of
    // trying to route from the current location without asking.
    return `geo:${lat},${lng}?q=${lat},${lng}(${encodedLabel})`;
  }
  // Web (and any other native runtime): Google Maps universal URL.
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
}

/**
 * Open a maps app pointing at the given coordinates. Falls back to
 * `openExternal` so unsupported / restricted schemes still degrade to
 * a Google Maps web link.
 */
export async function openInMaps({ lat, lng, label }) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  const url = pickMapsUrl({ lat, lng, label });
  if (isAndroid() && url.startsWith('geo:')) {
    // `geo:` isn't a browser scheme — hand it straight to the OS.
    try {
      if (typeof window === 'undefined') return false;
      window.location.href = url;
      return true;
    } catch { return false; }
  }
  return openExternal(url);
}
